import React, { useRef } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { toPng } from "html-to-image";
import { CircleFlag } from "react-circle-flags";

import {
  Column,
  Row,
  Label,
  H3,
  H4,
  P,
  Footer,
  MainWrapper,
  Metadata,
  Spinner,
  Grid,
  ListItem,
  Avatar,
  Anchor,
  HR,
  H1,
  H2,
  Button,
  SmothLineChart,
} from "../../components";
import { formatShortDates, formatNumber } from "../../utils";
import { HeaderAndDrawer, Counter } from "./components";
import { DonutChart } from "./components/DonutChart";
import { Color } from "../../utils/variables";

const DownloadImage = styled.div`
  width: 100%;
  border-radius: 10px;

  img {
    margin-top: -30px;
    padding: 0 30px;
  }
`;

const DownloadImageHeader = styled.div`
  background: linear-gradient(to right, #08281e, #114f3c);
  height: 122px;
  border-radius: 20px 20px 0 0;
  padding: 30px;
`;

const DownloadImageHeaderText = styled(H3)`
  color: white;
  background: ${Color("green", "6")};
  display: inline-block;
  padding: 0 5px;
`;

const DownloadImageFooter = styled.div`
  background: ${Color("gray", "1")};
  border-radius: 0 0 20px 20px;
  padding: 10px;
  display: flex;
  justify-content: center;
`;

// const Image = styled.div`
//   width: 100%;
//   background-image: url("/images/congress/download-image.svg");
//   background-size: cover;
//   background-position: center;
// `;

const HiddenImage = styled.div`
  height: 0;
  overflow: hidden;
  background: white;
`;

const HiddenImageHeader = styled.div`
  width: 1499px;
  height: 329px;
  position: relative;
  background: url("/images/congress/download-bkg.jpg");

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.8),
      rgba(0, 0, 0, 0.2)
    );
  }

  & > div {
    position: relative;
    width: 100%;
    z-index: 1;
  }

  .box {
    max-width: 145px;

    h4 {
      height: 48px;
      color: white;
    }
    hr {
      border-color: white;
    }
  }
`;

const HiddenImageTitle = styled(H1)`
  color: white;
  width: 430px;

  span {
    background: ${Color("green", "6")};
    padding: 0 5px;
    margin-right: 5px;
  }
`;

const Logo = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

const HiddenImageContent = styled(Row)`
  position: relative;
  z-index: 2;
  padding-left: 72px;
  padding-bottom: 72px;
  background: white;

  & > div {
    margin-top: -85px;
  }

  .box {
    background: white;
  }
`;

export const view = ({
  isAdmin,
  signUpClick,
  influentials,
  prolificTweeters,
  highestImpressions,
  // tweet,
  // isLoadingTweets,
  conference,
  conferenceByCountry,
  loadingConferenceByCountry,
  influencerStats,
  loadingInfluencerStats,
  timeSegmentedTweets,
  loadingTimeSegmentedTweets,
}) => {
  const imageRef = useRef(null);

  const htmlToImageConvert = () => {
    toPng(imageRef.current, { cacheBust: false })
      .then((dataUrl) => {
        const link = document.createElement("a");
        link.download = "congress-stats.png";
        link.href = dataUrl;
        link.click();
      })
      .catch((err) => {
        console.log(err);
      });
  };

  return (
    <MainWrapper className="dashboard">
      <Metadata />
      <HeaderAndDrawer
        {...{
          signUpClick,
          activeUrl: "congress_influencers",
          isAdmin,
        }}
      />
      <Column marginTop="100">
        <Column paddingBottom="100">
          <Column center>
            <Column maxWidth="1200" paddingX>
              <Column>
                <Anchor
                  bold
                  icon="arrowLeft"
                  to="/influencers/conference"
                  label="Back to top congresses"
                />
                <Column gap="10">
                  <H3 bold>{conference?.primaryName}</H3>
                  <P color="gray" fade="7">
                    Discover what healthcare providers are saying on social
                    media
                  </P>
                </Column>
              </Column>

              <HiddenImage>
                <Column ref={imageRef}>
                  <HiddenComponent
                    {...{
                      conference,
                      influencerStats,
                      influentials,
                      prolificTweeters,
                      highestImpressions,
                      conferenceByCountry,
                      loadingConferenceByCountry,
                      timeSegmentedTweets,
                      loadingTimeSegmentedTweets,
                    }}
                  />
                </Column>
              </HiddenImage>

              <Grid columns={2} gap="40">
                <Column maxWidth="600">
                  <Column>
                    {conference?.description && (
                      <P color="gray" fade="7">
                        {conference?.description}
                      </P>
                    )}
                    {conference?.description && (
                      <Column marginTop="10" gap="10">
                        <Label color="gray" fade="7">
                          {conference?.description}
                        </Label>
                        <Row gap="10" middle>
                          <Label bold color="gray" fade="7">
                            {formatShortDates(
                              conference?.startDate,
                              conference?.endDate
                            )}
                          </Label>
                          {conference?.url && (
                            <Anchor
                              bold
                              blank
                              to={conference.url}
                              label={conference.url
                                .replace("http://", "")
                                .replace("https://", "")}
                            />
                          )}
                        </Row>
                      </Column>
                    )}
                  </Column>

                  <InfluencerList
                    title="Top influencers"
                    subtitle="HCPs on X most mentioned by their peers"
                    influencers={influentials?.data}
                    isLoading={influentials?.isLoading}
                    isSuccess={influentials?.isSuccess}
                    counter="mentions"
                  />

                  <InfluencerList
                    title="Most active posters"
                    subtitle="HCPs on X with the highest number of posts"
                    influencers={prolificTweeters?.data}
                    isLoading={prolificTweeters?.isLoading}
                    isSuccess={prolificTweeters?.isSuccess}
                    counter="posts"
                  />

                  <InfluencerList
                    title="Highest impressions"
                    subtitle="HCPs on X whose posts generate the most views"
                    influencers={highestImpressions?.data}
                    isLoading={highestImpressions?.isLoading}
                    isSuccess={highestImpressions?.isSuccess}
                    counter="totalViews"
                  />
                </Column>

                <Column maxWidth="600" gap="20">
                  <DownloadImage>
                    <DownloadImageHeader>
                      <DownloadImageHeaderText bold>
                        CONGRESS <span>STATS</span>
                      </DownloadImageHeaderText>
                    </DownloadImageHeader>
                    <img
                      width="100%"
                      src="/images/congress/download-image.svg"
                      alt="image"
                    />
                  </DownloadImage>
                  <DownloadImageFooter>
                    <Button
                      label="Download"
                      disabled={
                        influentials?.isLoading ||
                        prolificTweeters?.isLoading ||
                        highestImpressions?.isLoading ||
                        loadingConferenceByCountry ||
                        loadingInfluencerStats ||
                        loadingTimeSegmentedTweets
                      }
                      onClick={htmlToImageConvert}
                    />
                  </DownloadImageFooter>

                  {loadingInfluencerStats ? (
                    <Row marginY="40" expand center>
                      <Spinner />
                    </Row>
                  ) : (
                    <Grid columns={2} gap="20" marginTop="20">
                      <Box title="Impressions">
                        <H1 bold color="green" fade="8">
                          {formatNumber(influencerStats?.postViews)}
                        </H1>
                      </Box>
                      <Box title="Posts on X">
                        <H1 bold color="green" fade="8">
                          {formatNumber(influencerStats?.postCount)}
                        </H1>
                      </Box>
                      <Box title="Participants">
                        <H1 bold color="green" fade="8">
                          {formatNumber(influencerStats?.accountCount)}
                        </H1>
                      </Box>
                      <Box title="Average posts per hour">
                        <H1 bold color="green" fade="8">
                          {formatNumber(influencerStats?.avgPostsPerHour)}
                        </H1>
                      </Box>
                      <Box title="Average posts per HCP">
                        <H1 bold color="green" fade="8">
                          {formatNumber(influencerStats?.avgPostsPerAccount)}
                        </H1>
                      </Box>
                    </Grid>
                  )}

                  {loadingConferenceByCountry ? (
                    <Row marginY="40" expand center>
                      <Spinner />
                    </Row>
                  ) : (
                    <Box bkg title="Posts per country" marginTop="20">
                      <DonutChart
                        data={conferenceByCountry}
                        id="mainDonutChart"
                        size={400}
                      />
                      <FlagList data={conferenceByCountry} />
                    </Box>
                  )}

                  {loadingTimeSegmentedTweets ? (
                    <Row marginY="40" expand center>
                      <Spinner />
                    </Row>
                  ) : (
                    <Box bkg title="Posts per day" marginTop="20">
                      <Column marginBottom="-20" marginX="-10">
                        <SmothLineChart
                          data={timeSegmentedTweets}
                          id="mainLineChart"
                          size={400}
                        />
                      </Column>
                    </Box>
                  )}
                </Column>
              </Grid>
            </Column>
          </Column>
        </Column>
        <Footer />
      </Column>
    </MainWrapper>
  );
};

const InfluencerList = ({
  influencers,
  isLoading,
  isSuccess,
  title,
  subtitle,
  counter,
  image,
}) => (
  <Column bkg="white">
    {image ? (
      <Column paddingAll gap="2" maxWidth="290">
        <Label large bold>
          {title}
        </Label>
        <Label small color="gray" fade="7">
          {subtitle}
        </Label>
      </Column>
    ) : (
      <Column marginTop="40" gap="2" maxWidth="580">
        <H4 bold>{title}</H4>
        <Label color="gray" fade="7">
          {subtitle}
        </Label>
      </Column>
    )}

    {isLoading && (
      <Row marginY="40" expand center>
        <Spinner />
      </Row>
    )}

    {isSuccess && influencers?.length === 0 && (
      <Row marginY="40" expand center>
        <Label color="gray" fade="7">
          No influencers found!
        </Label>
      </Row>
    )}

    {influencers?.length > 0 && (
      <Column
        gap="10"
        marginTop={image ? "5" : "20"}
        paddingX={image ? "20" : "0"}
      >
        {influencers?.map((dol, index) => {
          dol = dol.account ? dol.account : dol;
          return (
            <Column marginX="-10" key={dol.id}>
              <Link to={`/account/${dol.id}`}>
                <ListItem expand middle hover>
                  <Row gap="10" middle expand truncate>
                    <Row fit middle gap="10">
                      {!image && <Counter number={index + 1} />}
                      <Avatar
                        imageUrl={dol.imageURL}
                        name={dol.name}
                        large={!image}
                      />
                      {image ? (
                        <Column>
                          <Label large bold truncate>
                            {index + 1} {dol.name}
                          </Label>
                          <Row gap="5">
                            <Label small color="gray" fade="7">
                              @{dol.username}
                            </Label>
                            {counter === "totalViews" && (
                              <Label small color="gray" fade="7" bold noShrink>
                                {dol.totalViews === 1
                                  ? "1 view"
                                  : `${formatNumber(dol.totalViews)} views`}
                              </Label>
                            )}

                            {counter === "mentions" && (
                              <Label small color="gray" fade="7" bold noShrink>
                                {dol.mentions === 1
                                  ? "1 mention"
                                  : `${formatNumber(dol.mentions)} mentions`}
                              </Label>
                            )}

                            {counter === "posts" && (
                              <Label small color="gray" fade="7" bold noShrink>
                                {dol.mentions === 1
                                  ? "1 post"
                                  : `${formatNumber(dol.mentions)} posts`}
                              </Label>
                            )}
                          </Row>
                        </Column>
                      ) : (
                        <Column gap="5">
                          <Row gap="10" middle>
                            <H4 bold truncate>
                              {dol.name}
                            </H4>
                            <Label>@{dol.username}</Label>
                          </Row>
                          <Label color="gray" fade="7">
                            {dol.institutionName}
                            {dol.institutionName && dol.country && ", "}
                            {dol.country}
                          </Label>
                        </Column>
                      )}
                    </Row>

                    {!image && (
                      <>
                        {counter === "totalViews" && (
                          <Label color="gray" fade="7" bold noShrink>
                            {dol.totalViews === 1
                              ? "1 view"
                              : `${formatNumber(dol.totalViews)} views`}
                          </Label>
                        )}

                        {counter === "mentions" && (
                          <Label color="gray" fade="7" bold noShrink>
                            {dol.mentions === 1
                              ? "1 mention"
                              : `${formatNumber(dol.mentions)} mentions`}
                          </Label>
                        )}

                        {counter === "posts" && (
                          <Label color="gray" fade="7" bold noShrink>
                            {dol.mentions === 1
                              ? "1 post"
                              : `${formatNumber(dol.mentions)} posts`}
                          </Label>
                        )}
                      </>
                    )}
                  </Row>
                </ListItem>
              </Link>
            </Column>
          );
        })}
      </Column>
    )}
  </Column>
);

const Box = ({ title, children, marginTop, bkg }) => {
  return (
    <Column
      className="box"
      bkg={bkg ? "gray" : null}
      fade="1"
      paddingAll={bkg ? "20" : "0"}
      marginTop={marginTop}
    >
      <Column gap="10" marginBottom="10">
        <H4 bold color="green" fade="8">
          {title}
        </H4>
        <HR bkg="green" fade="8" />
      </Column>
      {children}
    </Column>
  );
};

const HiddenComponent = ({
  conference,
  influencerStats,
  influentials,
  prolificTweeters,
  highestImpressions,
  conferenceByCountry,
  loadingConferenceByCountry,
  timeSegmentedTweets,
  loadingTimeSegmentedTweets,
}) => {
  return (
    <Column>
      <HiddenImageHeader>
        <Column paddingAll="72">
          <Row gap="40">
            <HiddenImageTitle bold>
              <span>
                {conference?.primaryName?.split(" ").slice(0, -1).join(" ")}
              </span>
              {conference?.primaryName?.split(" ").slice(-1)}
            </HiddenImageTitle>
            <Box title="Impressions">
              <H2 bold color="white">
                {formatNumber(influencerStats?.postViews)}
              </H2>
            </Box>
            <Box title="Posts on X">
              <H2 bold color="white">
                {formatNumber(influencerStats?.postCount)}
              </H2>
            </Box>
            <Box title="Participants">
              <H2 bold color="white">
                {formatNumber(influencerStats?.accountCount)}
              </H2>
            </Box>
            <Box title="Average posts per hour">
              <H2 bold color="white">
                {formatNumber(influencerStats?.avgPostsPerHour)}
              </H2>
            </Box>
            <Box title="Average posts per HCP">
              <H2 bold color="white">
                {formatNumber(influencerStats?.avgPostsPerAccount)}
              </H2>
            </Box>
          </Row>
          <Logo>
            <img src="/images/congress/logo.png" alt="MW logo" />
          </Logo>
        </Column>
      </HiddenImageHeader>

      <HiddenImageContent gap="20">
        <InfluencerList
          image
          title="Top influencers"
          subtitle="HCPs on X most mentioned by their peers"
          influencers={influentials?.data}
          isLoading={influentials?.isLoading}
          isSuccess={influentials?.isSuccess}
          counter="mentions"
        />

        <InfluencerList
          image
          title="Most active posters"
          subtitle="HCPs on X with the highest number of posts"
          influencers={prolificTweeters?.data}
          isLoading={prolificTweeters?.isLoading}
          isSuccess={prolificTweeters?.isSuccess}
          counter="posts"
        />

        <InfluencerList
          image
          title="Highest impressions"
          subtitle="HCPs on X whose posts generate the most views"
          influencers={highestImpressions?.data}
          isLoading={highestImpressions?.isLoading}
          isSuccess={highestImpressions?.isSuccess}
          counter="totalViews"
        />

        <Column>
          {loadingConferenceByCountry ? (
            <Row marginY="40" expand center>
              <Spinner />
            </Row>
          ) : (
            <Box bkg title="Posts per country">
              <Row marginRight="-20">
                <FlagList data={conferenceByCountry} image />
                <DonutChart
                  data={conferenceByCountry}
                  id="hiddenDonutChart"
                  size={300}
                />
              </Row>
            </Box>
          )}

          {loadingTimeSegmentedTweets ? (
            <Row marginY="40" expand center>
              <Spinner />
            </Row>
          ) : (
            <Box bkg title="Posts per day" marginTop="-40">
              <Column marginBottom="-20" marginX="-10">
                <SmothLineChart
                  data={timeSegmentedTweets}
                  id="hiddenLineChart"
                  size={300}
                />
              </Column>
            </Box>
          )}
        </Column>
      </HiddenImageContent>
    </Column>
  );
};

const FlagList = ({ data, image }) => (
  <Column marginRight={image ? "0" : "20"}>
    {React.createElement(image ? Column : Grid, {
      ...(image ? { gap: "10", maxWidth: "100" } : { columns: 3, gap: "20" }),
      children: data?.map((item) => {
        if (item.country === "Other") {
          return null;
        }
        return (
          <Row key={item.country} middle gap="10">
            <CircleFlag countryCode={item.countrycode} height={20} />
            <Label small={image}>{item.country}</Label>
          </Row>
        );
      }),
    })}
  </Column>
);
